import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { nav, site } from "@/data/site";
import { IDS } from "@/constants/testIds";
import { useQuote } from "@/components/site/QuoteDialog";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { open: openQuote } = useQuote();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 text-ink ${
        scrolled
          ? "bg-cream/85 backdrop-blur-md border-b border-line shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-24 items-center justify-between">
          {/* Logo */}
          <a
            href="#home"
            data-testid={IDS.headerLogo}
            className="flex items-center gap-3.5 group"
          >
            <span className="inline-flex items-center justify-center rounded-full bg-white shadow-md ring-1 ring-line p-1.5 transition-transform duration-500 group-hover:scale-105">
              <img
                src={site.brand.logo}
                alt={site.brand.name}
                className="h-14 w-14 sm:h-16 sm:w-16 object-contain"
              />
            </span>
            <span className="font-heading tracking-tight leading-tight hidden sm:block">
              <span className="block text-2xl font-semibold leading-none text-forest">
                Ashrita Overseas
              </span>
            </span>
          </a>

          {/* Desktop nav */}
          <nav
            data-testid={IDS.headerNav}
            className="hidden lg:flex items-center gap-8"
          >
            {nav.map((n) => (
              <a
                key={n.href}
                href={n.href}
                className="text-sm font-medium text-ink/80 hover:text-brand transition-colors link-underline"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={openQuote}
              data-testid={IDS.headerCta}
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-brand text-white hover:bg-forest px-5 py-2.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 shadow-md shadow-brand/20"
            >
              Get a Quote <ArrowUpRight className="h-4 w-4" />
            </button>

            {/* Mobile toggle */}
            <button
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 text-ink"
              data-testid={IDS.mobileMenuToggle}
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-cream/95 backdrop-blur-md text-ink border-t border-line shadow-lg">
          <nav className="px-6 py-4 flex flex-col gap-2">
            {nav.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="py-2 text-base font-medium hover:text-brand transition-colors"
              >
                {n.label}
              </a>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                openQuote();
              }}
              className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-brand text-white px-5 py-3 text-sm font-semibold"
            >
              Get a Quote <ArrowUpRight className="h-4 w-4" />
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
